/*
 * One image of the justified rows of an image list (EventgalleryImageList.js): an item of
 * the list - the .thumbnail-container of the snippets - and the proportions of its image.
 *
 * The proportions come from the width and height attributes the server prints on the img
 * (an override of 6.x may print them as data-width/data-height), else from the --eg-ratio
 * of the item; once the browser has loaded the file, its own proportions win when they
 * differ by more than a percent, so a wrong size in the database never crops or distorts
 * an image. setSize() writes the box the rows give the item as custom properties, which
 * imagelist.less turns into the size and the margins of the item, and tells the browser
 * the width it draws the image in (the sizes attribute), so it picks the file for it from
 * the srcset of the img - unless the file the img has already is wide enough.
 */
import { currentWidth } from "../../common/js/srcset";

export default class Image {

    /**
     * @param {HTMLElement} element the item of the list
     */
    constructor(element) {
        this.element = element;
        this.img = element.querySelector('img');
        this.ratio = Image.readRatio(element, this.img);
    }

    /**
     * @param {HTMLElement} element
     * @param {HTMLImageElement|null} img
     * @returns {number} width divided by height, 1.5 when nothing says it
     */
    static readRatio(element, img) {
        const of = (width, height) => {
            const w = parseFloat(width);
            const h = parseFloat(height);
            return w > 0 && h > 0 ? w / h : 0;
        };
        let ratio = 0;
        if (img) {
            ratio = of(img.getAttribute('width'), img.getAttribute('height'))
                || of(img.getAttribute('data-width'), img.getAttribute('data-height'));
        }
        if (!ratio) {
            ratio = parseFloat(element.style.getPropertyValue('--eg-ratio')) || 0;
        }
        return ratio > 0 ? ratio : 1.5;
    }

    /**
     * Takes the proportions of the loaded file when they differ from the ones the page said.
     *
     * @returns {boolean} whether the proportions changed
     */
    takeNaturalRatio() {
        const img = this.img;
        if (!img || !img.complete || !img.naturalWidth || !img.naturalHeight) {
            return false;
        }
        const natural = img.naturalWidth / img.naturalHeight;
        if (Math.abs(natural / this.ratio - 1) <= 0.01) {
            return false;
        }
        this.ratio = natural;
        return true;
    }

    /**
     * Draws the item in its box of the rows.
     *
     * @param {{width: number, height: number, indent?: number, overhang?: number}} box in CSS
     *        pixels: the size, the space before the first item of a row beside the first
     *        image (indent) and the part of the first image which reaches into the rows
     *        below it (overhang, negative)
     */
    setSize(box) {
        // a list is laid out again while the browser parses it: the rows which stay are not
        // written again
        const key = [box.width, box.height, box.indent || 0, box.overhang || 0].join(' ');
        if (key === this.box) {
            return;
        }
        this.box = key;
        const style = this.element.style;
        style.setProperty('--eg-item-width', Image.px(box.width));
        style.setProperty('--eg-item-height', Image.px(box.height));
        Image.setOrRemove(style, '--eg-item-indent', box.indent);
        Image.setOrRemove(style, '--eg-item-overhang', box.overhang);

        this.width = Math.ceil(box.width);
        this.fitFile();
    }

    /**
     * Tells the browser the width the image is drawn in (sizes), so it picks the file for it -
     * unless the image has a file which is wide enough. Chromium fetches the file of a new sizes
     * attribute also when it is narrower, and an image which loads at once (the first row) has
     * its file from the estimate of the server (ThumbnailOptions::rows()) before this script
     * runs: it keeps that file while it loads and afterwards, when it is wide enough (the list
     * calls this again on its load). Until the review of #1894 every image of the first row was
     * fetched twice.
     */
    fitFile() {
        const img = this.img;
        if (!img || !this.width || img.getAttribute('sizes') === this.width + 'px') {
            return;
        }
        if (img.getAttribute('loading') !== 'lazy' && !img.complete) {
            return;
        }
        if (currentWidth(img) < this.width * (window.devicePixelRatio || 1)) {
            img.setAttribute('sizes', this.width + 'px');
        }
    }

    static setOrRemove(style, name, value) {
        if (value) {
            style.setProperty(name, Image.px(value));
        } else {
            style.removeProperty(name);
        }
    }

    /**
     * @param {number} value
     * @returns {string} the value in px with the precision of the layout of a browser
     */
    static px(value) {
        return (Math.round(value * 1000000) / 1000000) + 'px';
    }
}
